import type { MenuItem } from '@/types';

// SC-210: by default products are ordered by their code (SKU), ascending and
// numeric-aware so "41" < "42" < "410". Products without a code — e.g. those
// created via the "Copier produit" function, which leaves code empty — sort
// AFTER coded ones, ordered by name.
//
// SC-555: a category can opt into a manual order instead (its
// `useCustomProductOrder` flag), in which case products follow the position the
// operator dragged them to in the back-office. Equal positions fall back to the
// default rule, so a category that just opted in — every position still 0 —
// keeps the familiar order until the first manual reorder.
//
// Both rules mirror the admin catalogue and the owner dashboard
// (`sortProducts.ts` in each) so the back-office and the ordering site stay
// consistent.
const byNumericText = (a: string, b: string): number =>
  a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });

export function compareMenuItems(a: MenuItem, b: MenuItem): number {
  const ac = (a.code ?? '').trim();
  const bc = (b.code ?? '').trim();
  if (ac && bc) {
    const byCode = byNumericText(ac, bc);
    return byCode !== 0 ? byCode : byNumericText(a.name, b.name);
  }
  if (ac) return -1;
  if (bc) return 1;
  return byNumericText(a.name, b.name);
}

export function compareMenuItemsByPosition(a: MenuItem, b: MenuItem): number {
  const byPosition = (a.position ?? 0) - (b.position ?? 0);
  return byPosition !== 0 ? byPosition : compareMenuItems(a, b);
}

/**
 * Returns a new array in display order; does not mutate input. `useCustomOrder`
 * comes from the items' category (SC-555); omitted = the SC-210 code→name rule.
 */
export function sortMenuItems(items: MenuItem[], useCustomOrder = false): MenuItem[] {
  return items.slice().sort(useCustomOrder ? compareMenuItemsByPosition : compareMenuItems);
}
